'use client';

import { forwardRef } from 'react';
import { FileInput as MantineFileInput } from '@mantine/core';
import type { FileInputProps as MantineFileInputProps } from '@mantine/core';

import type { LockedProps } from '../types';

type BaseFileInputProps<Multiple extends boolean = false> = Omit<
  MantineFileInputProps<Multiple>,
  LockedProps
>;

type AnyFileInputProps = BaseFileInputProps<false> | BaseFileInputProps<true>;

/**
 * Props for {@link FileInput}.
 *
 * **When to use this component:** Prefer **`FileButton`** from `@highlight-cp/core-ui` for almost
 * all file picking. Use **`FileInput`** when the user may upload **multiple** files (`multiple`);
 * for single-file flows, **`FileButton`** is usually the better fit.
 *
 * **Custom props:** Same as Mantine `FileInput` minus the {@link LockedProps} style overrides;
 * visuals come from theme and shared input module styles. Use `multiple` to switch between
 * `File | null` and `File[]` value shapes (see Mantine docs).
 */
export type FileInputProps<Multiple extends boolean = false> = BaseFileInputProps<Multiple>;

/**
 * ### FileInput
 *
 * @description
 * Mantine's FileInput minus per-call-site style overrides (see {@link LockedProps}). Visual
 * defaults come from the app theme and shared `Input` styles in `@highlight-cp/theme`. Label,
 * error, and description order defaults to the shared form layout; pass `inputWrapperOrder` to
 * change it.
 *
 * **Prefer `FileButton`** from `@highlight-cp/core-ui` for almost all single-file picking; reach
 * for `FileInput` when **multiple** files may be selected (`multiple`).
 *
 * @example
 * <FileInput label="Upload" accept=".csv" value={file} onChange={setFile} />
 *
 * @param props - {@link FileInputProps}
 * @returns React element
 */
const FileInput = forwardRef(function FileInput(
  props: AnyFileInputProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <MantineFileInput
      ref={ref}
      inputWrapperOrder={['label', 'input', 'error', 'description']}
      {...(props as MantineFileInputProps)}
    />
  );
});

FileInput.displayName = 'FileInput';

export default FileInput;
